import T from 'i18n-react'
import { isArray, isEmpty, cloneDeep, curry } from 'lodash'
import { createSelector } from 'reselect'
import { compose } from 'redux'
import * as R from 'ramda'
import P from 'helpers/predicates'

import { keysToCamelCase } from 'helpers/RequestUtil'
import { entitySelector } from 'containers/Entity/selectors'

const elementSelector = state => ({...state.form})

/**
 * getValuesFromEntity
 *
 * @param entityId
 * @param entityType
 * @param entities
 * @returns object obtained from entity tables
 */
const getValuesFromEntity = (entityId, entityType, entities) => {
  const { type, ...rest } = entities[entityType][entityId]
  return { ...rest }
}

/**
 * replaceRefWithUndefined
 * Replace property with undefined
 *
 * @param property
 * @param elements
 * @returns {undefined}
 */
const replaceRefWithUndefined = curry((property, source, elements) => {
  Object.keys(elements)
    .filter(key => isEmpty(elements[key][source]))
    .forEach(key => {
      let element = elements[key]
      element[property] = undefined
    }
  )
  return elements
})

/**
 * replaceRefWithVal
 * Replace reference with their entity equivalent values
 * Makes sure that the property exists and updates references with values
 *
 * @param property
 * @param source
 * @param elements
 * @param entities
 * @returns {undefined}
 */
const replaceRefWithVal = curry((property, source, entities, elements) => {
  Object.keys(elements)
    .filter(key => !isEmpty(elements[key][source]))
    .forEach(key => {
      let element = elements[key]
      element[property] = element[source].map(
        ({ id, type }) => getValuesFromEntity(id, type, entities)
      )
    }
  )
  return elements
})

/**
 * replaceValues
 * Replace Values with Entity Values
 *
 * @param 'values'
 * @returns {undefined}
 */
const replaceWith = curry((property, source, entities) => {
  const replacePropertyWithValues = replaceRefWithVal(property, source, entities)
  const replacePropertyWithUndefined = replaceRefWithUndefined(property, source)
  return compose(
    replacePropertyWithValues,
    replacePropertyWithUndefined
  )
})

/**
 * copyAndReplace
 *
 * Takes all form elements from entity tree
 * and saves within forms
 *
 * @param replaceFunction
 * @param entities
 * @returns {undefined}
 */
const copyAndReplace = curry((replaceFunction, entities) => {
  let elements
  if (entities.elements){
    const entityElements = cloneDeep(entities.elements)
    elements = replaceFunction(entities)(entityElements)
  }
  return elements
})

const replaceValuesWithDefaults = replaceWith('values', 'defaults')
const replaceValuesWithValues = replaceWith('values', 'values')

const copyAndReplaceValuesWithDefaults = copyAndReplace(replaceValuesWithDefaults)
const copyAndReplaceValuesWithValues = copyAndReplace(replaceValuesWithValues)

/**
 * filteredElementsSelector
 *
 * Sorts based on the element order provided
 *
 * @param elementSelector
 * @param elementOrder
 * @returns object with keys as the element ID, and values as the element
 * values
 */
const filteredElementsSelector = (elementOrder) => (
  createSelector(
    elementSelector,
    elements => Object.keys(elements)
      .reduce((result, key) => {
        const element = elements[key]
        if (elementOrder.hasOwnProperty(element.label)){
          result[key] = { ...element }
        }
        return result
      }, {})
  )
)

/**
 * categoriesSelector
 *
 * Get unique categories and prioritizes their order
 * based on provided order
 *
 * @param categoryOrder
 * @returns array of categories
 */
const categoriesSelector = (categoryOrder) => (
  createSelector(
    elementSelector,
    elements => {
      const categories = Object.values(elements).map(element => element.category)
      return [ ...new Set(categories)]
        .filter(category => categoryOrder.hasOwnProperty(category))
        .sort((a, b) => categoryOrder[a] - categoryOrder[b])
    }
  )
)

/**
 * formattedCategoriesSelector
 *
 * formats the categories in the correct order
 *
 * @param categoryOrder
 * @returns Array of strings
 */

const formattedCategoriesSelector = (categoryOrder) => (
  createSelector(
    categoriesSelector(categoryOrder),
    categories => categories.map(category => T.translate(category))
  )
)


/**
 * formattedFormElementsSelector
 *
 * Translates the label and category for form elements
 *
 * @returns object of elements
 */
const formattedFormElementsSelector = createSelector(
  elementSelector,
  elements => Object.keys(elements)
    .reduce((result, key) => {
      const element = elements[key]
      result[key] = {
        ...element,
        label: T.translate(element.label),
        category: T.translate(element.category)
      }
      return result
    }, {})
)

/**
 * categoryElementsSelector
 *
 * Get unique categories and prioritize their order and their elements order
 *
 * @param categoriesSelectorcategoryOrder
 * @returns Array of objects that hold the category and all related elements
 */
const categoryElementsSelector = (categoryOrder, elementOrder) => (
  createSelector(
    categoriesSelector(categoryOrder),
    filteredElementsSelector(elementOrder),
    (categories, elements) => {
      const elementKeys = Object.keys(elements)
      return categories.map((category) => {
        // get all elements with category as the same
        const sortedIds = Object.entries(elements)
          .filter(([key, { category: elementCategory }]) => elementCategory === category)
          .sort(([keyA, { label: labelA }], [keyB, { label: labelB }]) => elementOrder[labelA] - elementOrder[labelB])
          .map(([key]) => key)

        return {
          category: T.translate(category),
          elementIds: sortedIds
        }
      }).filter(({ elementIds }) => elementIds.length !== 0)
    }
  )
)

/*
 * Make sure you take your form elements values and compress the data
 * transform values
 *
 * 1. If object doesnt' have a property name save within the form using the
 * model name
 * 2. If the object does have a property name, make sure to change the
 *    value property to the proeperty name if this fails than its an error
 * 3. Merge with the arrays together (technically the array length should
 *    be 1
 */
const formatProjectData = (elements) => {
  /*const elementValues = Object.values(elements)
  return cloneDeep(elementValues).filter(({values}) => values
  ).map(element => {
    const { propertyName, values: oldValues } = element
    let values = oldValues
    if (!isArray(values)) values = [oldValues]
    return { ...element, values }
  }).map(element => {
    const { propertyName, values: oldValues } = element
    let values = oldValues
    if (propertyName) {
      if (values.length > 1)
        throw Error('Error with creating converting propertyName, length of array is greater than 1')
      if (values[0])
        values = [{[propertyName]: values[0].value}]
    }
    return { ...element, values }
  }).reduce((result, element) => {
    const { modelName, values } = element
    let model = result[modelName] || []
    model = values.reduce((result, value, index) => {
      result[index] = Object.assign({}, value, result[index])
      return result
    }, model)
    return { ...result, [modelName]: model }
  }, {})*/
  const elementValues = Object.values(elements)
  const projectNameElement = elementValues.find((element) => element.key == 'project_name')
  const projectTypeElement = elementValues.find((element) => element.key == 'project_type')
  const projectRestrictedElement = elementValues.find((element) => element.key == 'project_restricted')
  if(projectNameElement && projectTypeElement && projectRestrictedElement){
    return {
      project: [{
        name: projectNameElement.values[0].value,
        type: projectTypeElement.values[0].value,
        restricted: projectRestrictedElement.values[0].value
      }]
    }
  }
  throw 'Project values do not exist'
}

const getElementTypes = (object, key, final) => {
  const type = object.type
  if (type === 'object') {
    const pairs = R.compose(
      R.forEach(([key, nextObject]) => getElementTypes(nextObject, key, final)),
      R.toPairs
    )(object.properties)
  } else if (type === 'array') {
    getElementTypes(object.items, 'items', final)
  } else {
    final[key] = type
  }
  return final
}

const formatElements = (elements) => {
  const toBool = R.cond([
    [R.is(String), R.compose((val) => val == 'true', R.toLower)],
    [R.is(Boolean), R.identity]
  ])

  const convertTo = R.cond([
    [R.equals('boolean'), R.always(toBool)],
    [R.equals('string'), R.always(val => val.toString())],
    [R.equals('number'), R.always(parseFloat)],
    [R.equals('integer'), R.always(parseInt)],
    [R.T, R.always(R.identity)]
  ])

  const removeEmptyAttrs = R.compose(
    R.fromPairs,
    R.filter(([property, value]) => R.complement(R.either(R.isNil, R.isEmpty))(value)),
    R.toPairs
  )

  const convertObjectTypes = (types) => R.compose(
    R.fromPairs,
    R.map(([property, value]) => [property, convertTo(types[property])(value)]),
    R.map(([property, value]) => [property, Array.isArray(value) ? R.map(removeEmptyAttrs, value) : value]),
    R.filter(([property, value]) => R.complement(R.either(R.isNil, R.isEmpty))(value)),
    R.toPairs
  )

  return R.reduce(
    (final, {values, properties, key}) => {
      if (properties.values) {
        const types = getElementTypes(properties.values, 'values', {})
        const formattedValues = R.map(convertObjectTypes(types))(values)
        if (properties.values.type === 'object')
          final[key] = R.isEmpty(formattedValues) ? {} : R.head(formattedValues)
        else
          final[key] = formattedValues
      }
      return final
    },
    {}
  )(elements)
}

const formatRevisionData = R.converge(
  R.merge,
  [
    R.prop('revision'),
    R.compose(keysToCamelCase, formatElements, R.values, R.prop('form'))
  ]
)

const revisionEntitySelector = createSelector(
  entitySelector,
  R.prop('revisions')
)

const elementsEntitySelector = createSelector(
  entitySelector,
  R.prop('elements')
)

export {
  elementSelector,
  copyAndReplaceValuesWithDefaults,
  copyAndReplaceValuesWithValues,
  formattedFormElementsSelector,
  formattedCategoriesSelector,
  categoryElementsSelector,
  filteredElementsSelector,
  categoriesSelector,
  formatProjectData,
  formatRevisionData,
  revisionEntitySelector,
  elementsEntitySelector,
}
